import React, { useEffect, useRef, useState } from "react";
import { AirbudsYearlyHeader } from "../component/storyHeader";

import { motion } from "motion/react";
import { StatsIntro } from "./statsIntro";
import { StatsMostPlayedSingle } from "./statsMostPlayedSingle";
import { StatsMostPlayedList } from "./statsMostPlayedList";
import { TracksPlayed } from "./statsTrackPlayed";
import ShareableComponent from "../sharing";
import { useData } from "../data/dataContext";
import StoryProgressBar from "../component/pagination";
import { StoryOverlay } from "../compat/compat";

import { useNavigate, NavLink, Link } from "react-router";
import { EmptyScreen } from "../validation/validation";
import EmojiWave, { defaultEmojis, questionEmojis } from "../compat/emojiWave";
import { Counter } from "../component/counter";
import { useMusicPlayer } from "../music/musicPlayerContext";

function useStateAndRef(initial) {
  const [value, setValue] = useState(initial);
  const valueRef = useRef(value);
  valueRef.current = value;
  return [value, setValue, valueRef];
}

export const Stats = ({}) => {
  const { data } = useData();
  const [statsStep, setStatsStep, refStep] = useStateAndRef(0);
  const navigate = useNavigate();
  const [categories, setCategories, refCategories] = useStateAndRef([]);
  const [reactionsList, setReactionsList] = useState(defaultEmojis);

  const { setMusicURL, playMusic } = useMusicPlayer();
  const [showMusic, setShowMusic] = useState(false);
  const [currentSongIndex, setCurrentSongIndex, refCurrentSong] =
    useStateAndRef(0);

  const previous = () => {
    setStatsStep(Math.max(statsStep - 1, 0));
  };

  const next = () => {
    if (statsStep == categories.length - 1) {
      navigate("/menu");
    } else {
      setStatsStep(Math.min(statsStep + 1, categories.length));
    }
  };

  useEffect(() => {
    if (data && data.reactions) {
      const reactions = data.reactions.sample
        .filter((reaction) => reaction.kind === "emoji")
        .map((reaction) => reaction.body);
      setReactionsList(reactions.concat(reactions));
    }
  }, [data]);

  useEffect(() => {
    if (data && data.top_songs && data.top_songs.length > 0) {
      setShowMusic(true);
      setMusicURL(data.top_songs[0].song_id.preview);
    }

    return () => {
      setMusicURL(null);
    };
  }, [data]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        data &&
        data.top_songs &&
        data.top_songs.length > refCurrentSong.current + 1
      ) {
        const songIndex = refCurrentSong.current + 1;
        setCurrentSongIndex(songIndex);
        setShowMusic(true);
        setMusicURL(data.top_songs[songIndex].song_id.preview);
      } else {
        setCurrentSongIndex(0);
        setShowMusic(false);
        setMusicURL(null);
      }

      return () => {
        setMusicURL(null);
      };
    }, 20000);

    //Clearing the interval
    return () => clearInterval(interval);
  }, [data]);

  useEffect(() => {
    var c = [];

    c.push({
      id: "intro",
    });

    c.push({
      id: "intro-2",
    });

    if (data && data.top_albums && data.top_albums.length > 0) {
      c.push({
        id: "single-album",
      });
    }

    if (data && data.top_albums && data.top_albums.length > 1) {
      c.push({
        id: "list-albums",
      });
    }

    if (data && data.top_artists && data.top_artists.length > 0) {
      c.push({
        id: "single-artist",
      });
    }

    if (data && data.top_artists && data.top_artists.length > 1) {
      c.push({
        id: "list-artists",
      });
    }

    if (data && data.top_songs && data.top_songs.length > 0) {
      c.push({
        id: "single-song",
      });
    }

    if (data && data.top_songs && data.top_songs.length > 1) {
      c.push({
        id: "list-songs",
      });
    }

    if (data && data.top_genres && data.top_genres.length > 0) {
      c.push({
        id: "list-genres",
      });
    }

    if (data && data.play_count) {
      c.push({
        id: "count-tracks",
      });
    }

    if (data && !data.top_genres && !data.top_artists) {
      c.push({
        id: "reactions",
        type: "reactions",
      });

      c.push({
        id: "questions",
        type: "questions",
      });
    }

    c.push({
      id: "share",
    });

    setCategories(c);
  }, [data]);

  if (!data) {
    return <EmptyScreen text="loading..." />;
  }

  const renderState = (ctgs, state) => {
    if (!ctgs || ctgs.length <= state) {
      return (
        <EmptyScreen text="loading..." closeAction={() => navigate("/menu")} />
      );
    }

    const stepId = ctgs[state].id;

    switch (stepId) {
      case "intro":
        return <StatsIntro data={data} next={() => {}} />;
      case "intro-2":
        return (
          <motion.div
            key="step-1"
            className="app-stats-content"
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 1, y: "-100%" }}
            transition={{
              duration: 1.0,
              delay: 0.0,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            <h3 className="title title-stats">your</h3>
            <img
              src={require("../assets/img/chrome2024.png")}
              alt="airbuds yearly recap"
              className="full-centered-img"
            />
            <h1 className="title subtile-stats">Music Recap</h1>
          </motion.div>
        );
      case "single-album":
        return (
          <motion.div
            key="step-2"
            className="app-stats-content "
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 1, y: "-100%" }}
            transition={{
              duration: 1.0,
              delay: 0.0,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            <StatsMostPlayedSingle
              type="album"
              data={data}
              next={() => setStatsStep(statsStep + 1)}
            />
          </motion.div>
        );
      case "list-albums":
        return (
          <StatsMostPlayedList
            type="albums"
            data={data}
            next={() => setStatsStep(statsStep + 1)}
          />
        );
      case "single-artist":
        return (
          <motion.div
            key="step-2"
            className="app-stats-content "
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 1, y: "-100%" }}
            transition={{
              duration: 1.0,
              delay: 0.0,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            <StatsMostPlayedSingle
              type="artist"
              data={data}
              next={() => setStatsStep(statsStep + 1)}
            />
          </motion.div>
        );

      case "list-artists":
        return (
          <StatsMostPlayedList
            type="artists"
            data={data}
            next={() => setStatsStep(statsStep + 1)}
          />
        );
      case "single-song":
        return (
          <motion.div
            key="step-2"
            className="app-stats-content "
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 1, y: "-100%" }}
            transition={{
              duration: 1.0,
              delay: 0.0,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            <StatsMostPlayedSingle
              type="song"
              data={data}
              next={() => setStatsStep(statsStep + 1)}
            />
          </motion.div>
        );

      case "list-songs":
        return (
          <StatsMostPlayedList
            type="songs"
            data={data}
            next={() => setStatsStep(statsStep + 1)}
          />
        );
      case "list-genres":
        return (
          <StatsMostPlayedList
            type="genres"
            data={data}
            next={() => setStatsStep(statsStep + 1)}
          />
        );
      case "count-tracks":
        return (
          <TracksPlayed data={data} next={() => setStatsStep(statsStep + 1)} />
        );
      case "share":
        return <ShareableComponent data={data} />;
      case "reactions":
        if (!data.reactions) {
          next();
        }

        return (
          <div>
            <EmojiWave emojis={reactionsList} next={() => {}} />
            <motion.div
              key="step-1"
              className="app-curiosity-content"
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: "0%" }}
              exit={{ opacity: 0, y: "-100%" }}
              transition={{
                duration: 1.0,
                delay: 0.5,
                ease: [0, 0.71, 0.2, 1.01],
              }}
            >
              <div className="column-layout">
                <Counter
                  className="headline counter-big"
                  from={0}
                  to={data.reactions.count}
                  duration={3}
                />
                <h2>Reactions</h2>
                <h3 className="light-headline">Sent & received</h3>
              </div>
            </motion.div>
          </div>
        );
      case "questions":
        if (
          !data.prompt_stats ||
          data.prompt_stats.answers_count == null ||
          data.prompt_stats.prompts_count == null
        ) {
          next();
        }

        return (
          <div>
            <EmojiWave
              key="question-emojis"
              emojis={questionEmojis}
              next={() => {}}
            />
            <motion.div
              key="step-2"
              className="app-curiosity-content"
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: "0%" }}
              exit={{ opacity: 0, y: "-100%" }}
              transition={{
                duration: 1.0,
                delay: 0.5,
                ease: [0, 0.71, 0.2, 1.01],
              }}
            >
              <div className="column-layout">
                {data.prompt_stats && data.prompt_stats.answers_count && (
                  <Counter
                    className="headline counter-big"
                    from={0}
                    to={
                      data.prompt_stats.answers_count +
                      data.prompt_stats.prompts_count
                    }
                    duration={3}
                  />
                )}
                <h2>Questions</h2>
                <h3 className="light-headline">sent & answered</h3>
              </div>
            </motion.div>
          </div>
        );
      default:
        return <ShareableComponent data={data} />;
    }
  };

  return (
    <div
      className={
        "app-stats-container stats-gradient-background " +
        (statsStep == categories.length - 1 ? "share-background" : "")
      }
    >
      {renderState(categories, statsStep)}
      <StoryOverlay
        className={statsStep == categories.length - 1 ? "not-clickable" : ""}
        prev={previous}
        next={next}
      />
      <AirbudsYearlyHeader
        closeAction={() => {
          navigate("/thanks");
        }}
        showMusic={showMusic}
      />
      <StoryProgressBar
        className={"story-bar"}
        totalPages={categories.length}
        currentPage={statsStep}
        duration={0}
      />
    </div>
  );
};
